import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast } from "../../components/Toast/message";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { IMPLEMENTED_TRIGGERS } from "./triggers";

// classify a trigger the same way the Trigger/Intent modal does — derived from the same
// single source of truth as the offer form's trigger dropdown, not a separate hardcoded list.
const INTENT = IMPLEMENTED_TRIGGERS.filter((t) => t.type === "Intent").map((t) => t.value);
const ACTION = IMPLEMENTED_TRIGGERS.filter((t) => t.type === "Action").map((t) => t.value);
const ALL_TRIGGERS = [...INTENT, ...ACTION];

const n = (v) => (v == null ? 0 : Number(v));
const pct = (a, b) => (n(b) > 0 ? ((n(a) / n(b)) * 100).toFixed(1) + "%" : "-");

const PopupStatsModal = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [trigger, setTrigger] = useState("");
  const [offers, setOffers] = useState([]);
  const [availableTriggers, setAvailableTriggers] = useState([]); // only triggers present in impressions

  useEffect(() => {
    if (open) {
      setTrigger("");
      fetchStats("", true);
    }
  }, [open]);

  const fetchStats = async (trig, setAvail = false) => {
    const params = [];
    if (trig) params.push(`trigger=${encodeURIComponent(trig)}`);
    let url = endPoints.api.POPUP_STATS + (params.length ? `?${params.join("&")}` : "");
    setIsLoading(true);
    try {
      const res = await getAllData(url);
      if (res?.status === "success") {
        const list = res?.data?.offers || [];
        setOffers(list);
        if (setAvail) {
          const seen = [];
          list.forEach((o) => {
            const t = o?.triggerEvent;
            if (t && !seen.includes(t)) seen.push(t);
          });
          setAvailableTriggers(seen);
        }
      } else {
        ErrorToast("Failed to load stats");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const apply = () => fetchStats(trigger);

  const group = (key) => (offers || []).filter((o) => (key === "action" ? ACTION : INTENT).includes(o?.triggerEvent));
  const other = (offers || []).filter((o) => !ALL_TRIGGERS.includes(o?.triggerEvent));

  const StatRows = ({ list }) => (
    <>
      {list.map((o, i) => (
        <TableRow key={`${o?.popupId}-${i}`} hover>
          <TableCell>{o?.name || `#${o?.popupId}`}</TableCell>
          <TableCell><span style={{ color: "#B9A7F5", fontSize: 12, fontFamily: "monospace" }}>{o?.triggerEvent}</span></TableCell>
          <TableCell align="center">{n(o?.shown)}</TableCell>
          <TableCell align="center">{n(o?.users)}</TableCell>
          <TableCell align="center">{n(o?.clicked)}</TableCell>
          <TableCell align="center">{n(o?.dismissed)}</TableCell>
          <TableCell align="center">{pct(o?.clicked, o?.shown)}</TableCell>
        </TableRow>
      ))}
    </>
  );

  const Section = ({ label, list }) =>
    list.length > 0 && (
      <>
        <TableRow>
          <TableCell colSpan={7} sx={{ backgroundColor: "action.hover", color: "text.primary", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: ".05em" }}>
            {label}
          </TableCell>
        </TableRow>
        <StatRows list={list} />
      </>
    );

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
      <DialogTitle sx={{ pr: 6 }}>
        Pop-up Stats
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "#64607a" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Toaster />
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
          <TextField
            select
            size="small"
            label="Trigger / Intent"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            style={{ minWidth: 220 }}
            disabled={availableTriggers.length === 0}
          >
            <MenuItem value="">All</MenuItem>
            {availableTriggers.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={apply}>Apply</Button>
        </div>

        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold", minWidth: 160 }}>Offer</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Trigger</TableCell>
                <TableCell align="center" style={{ fontWeight: "bold" }}>Shown</TableCell>
                <TableCell align="center" style={{ fontWeight: "bold" }}>Users</TableCell>
                <TableCell align="center" style={{ fontWeight: "bold" }}>Clicked</TableCell>
                <TableCell align="center" style={{ fontWeight: "bold" }}>Dismissed</TableCell>
                <TableCell align="center" style={{ fontWeight: "bold" }}>CTR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!offers || offers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} style={{ color: "#999" }}>No impressions yet for this filter.</TableCell>
                </TableRow>
              )}
              <Section label="Intent-based" list={group("intent")} />
              <Section label="Action-based" list={group("action")} />
              <Section label="Other" list={other} />
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopupStatsModal;
